<template>
  <b-container fluid class="chat-box">
    <b-row>
      <b-col cols="12" lg="12" class="w-100">
        <div
          v-for="(msg, msgidx) in messages"
          :key="msgidx"
          class="chat-record mt-2"
        >
          <ChatMessageComponent
            :style="{
              '--main-color': mainColor,
              '--secondary-color': secondaryColor,
            }"
            :initMsg="msg"
            :key="msgidx"
            :msgidx="msgidx"
            :ref="`msgRef-${msg.name}`"
            :context="context"
            @select-card="handleCardClick"
            @increase="handleIncrease"
            @decrease="handleDecrease"
            @continue="continueChat"
            @scroll-to-bottom="scrollToBottom"
            @scroll-to-msg="scrollToMsg"
            @select-checkbox="handleCheckbox"
            @set-text-input="setTextInput"
            @selector-change="handleSelectorChange"
            @go-to-step="goToStep"
          />
          <div>
            <b-row
              v-if="msg.isFinal"
              class="w-100 reset-button py-2 m-1"
              @click="resetChat"
            >
              <b-col>Reiniciar chat</b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
// Studentless "individual documents" chatbot. Flow:
//   document_type -> grade -> [diagnosis] -> user_role -> [first page] -> fill
// The user picks a document type (id + name only), then the student's grade
// (kept in context — some online templates use it to show special options).
// We then load the type's templates: a single template is used directly,
// otherwise the user picks the diagnosis to resolve the right template. Then
// they fill the real online document inline (DocumentFillStep ->
// DynamicDocumentForm) and download the rendered Word. No Document /
// StudentGrade is ever persisted. Same chat engine as ChatbotTestCorrection.
import ChatMessageComponent from "./ChatMessageComponent.vue";
import { MEETING_OPTION, meetingQuestion } from "./meetingOffer";
import { referralOptions, referralQuestions } from "./referralOffer";
import { ensureReferralRewards } from "./referralRewards";
import { usesLine, usesCardClass, spendsUses } from "./serviceUses";
import { skipAutoScroll } from "./chatScroll";
const SERVICE = "documents";
import DocumentFillStep from "./DocumentFillStep.vue";
import MissionsView from "@/components/utopie/miscellaneous/MissionsView";
import individualDocumentService from "@/services/individualDocumentService";
import informationService from "@/services/informationService";

// Entry menu, same three options as the Pruebas Informales chatbot.
//
// A function, not a const: the referral entries depend on what `check_referred`
// answered, and a module-level array would freeze that answer as "unknown" at
// import time and never drop them.
const menuOptions = () => [
  { label: "Trabajar en un documento", nextIndex: "document_type" },
  { label: "Recibir una cotización para mi colegio", nextIndex: "bill" },
  // Las formas de conseguir usos van en este orden a propósito: invitar
  // primero (es la que más usos reparte y trae gente nueva), luego declarar
  // quién te invitó, y después el resto.
  ...referralOptions(SERVICE),
  { label: "Comprar usos", redirect: "/utopie#/buy_product/documents" },
  { label: "Ver misiones y ganar usos", nextIndex: "missions" },
  MEETING_OPTION,
];

const menuAnswers = (options) =>
  options.map((ans) => ({
    name: ans.label,
    isSelected: false,
    class: "col-md-7 col-12 my-2",
    bootstrap_classes: "text-left px-2 font-weight-bold",
    nextQuestionIndex: ans.nextIndex,
    redirect: ans.redirect,
    cardClass: usesCardClass(ans, SERVICE),
  }));

// Keep only the "main" diagnosis templates: a diagnosis whose value is a
// multiple of 100 is a main diagnosis (sub-diagnoses are 101, 102, …), deduped
// by diagnostic. Falls back to all (deduped) when none are main, so a type with
// only sub-diagnosis templates still works.
function mainDiagnosisTemplates(templates) {
  const isMain = (t) =>
    t.diagnostic != null && Number(t.diagnostic) % 100 === 0;
  const dedupe = (list) => {
    const seen = new Set();
    return list.filter((t) => {
      if (seen.has(t.diagnostic)) return false;
      seen.add(t.diagnostic);
      return true;
    });
  };
  const main = dedupe(templates.filter(isMain));
  return main.length ? main : dedupe(templates);
}

export default {
  components: { ChatMessageComponent },
  name: "ChatbotIndividualDocuments",
  data() {
    return {
      mainColor: "#208ac6",
      secondaryColor: "#88B1EB",
      currentQuestionIndex: "welcomeMessage",
      context: {
        service: "Documentos PIE",
        schoolId: null,
      },
      messages: [],
      questions: {},
    };
  },
  created() {
    // Se pregunta acá, al montar, y no cuando la persona abre el referido: el
    // guard que decide si el referido se ofrece (`referralRewardsKnownEmpty`)
    // sólo se enteraba dentro de la vista de invitar, o sea después del clic
    // que queremos evitar. Es idempotente por servicio y falla abierto.
    ensureReferralRewards(SERVICE);
    this.questions = {
      welcomeMessage: {
        name: "welcomeMessage",
        text: `¡Hola ${this.$store.state.login.first_name}! Aquí podrás responder los documentos PIE en línea y descargar el Word con tus respuestas.<br/>${usesLine(
          SERVICE
        )}<br/><strong>¿Qué te gustaría hacer?</strong>`,
        type: "options",
        single_choice: true,
        answersLoaded: false,
        nextQuestionIndex: "document_type",
        answers: [],
        getAnswers() {
          this.answers = menuAnswers(menuOptions());
          this.answersLoaded = true;
          return Promise.resolve(this.answers);
        },
        routine() {},
      },
      // Same menu, shown again after a cotización / the missions view.
      mainMessage: {
        name: "mainMessage",
        text: () => `${usesLine(SERVICE)}<br/>Ahora, <strong>¿qué quieres hacer?</strong>`,
        type: "options",
        single_choice: true,
        answersLoaded: false,
        nextQuestionIndex: "document_type",
        answers: [],
        getAnswers(context) {
          // Ya usada = ya leída: no se vuelve a ofrecer. La única que repite es
          // la acción del servicio (trabajar en un documento), sin la cual el
          // menú se quedaría sin su razón de existir.
          this.answers = menuAnswers(
            menuOptions()
              .filter(
                (ans) => !(ans.nextIndex in context) || spendsUses(ans)
              )
              .concat([{ label: "Reiniciar chat", nextIndex: "reset" }])
          );
          this.answersLoaded = true;
          return Promise.resolve(this.answers);
        },
        routine() {},
      },
      bill: {
        name: "bill",
        text: "¡Excelente! Indícanos tu correo y la cantidad de documentos a cotizar y te enviaremos la cotización.",
        type: "inputs",
        answersLoaded: true,
        answers: [
          {
            name: "email",
            type: "email",
            placeholder: "Escribe tu correo aquí",
            class: "col-12 col-md-6 mb-2",
          },
          {
            name: "applications",
            type: "number",
            placeholder: "Cantidad de documentos",
            class: "col-12 col-md-6 mb-2",
          },
        ],
        nextMessage: "Obtener cotización!",
        extraFunction: (data) => {
          informationService
            .sendEurekaBill({
              email: data.email,
              applications: data.applications,
              service: "DOCUMENT",
            })
            .then((resp) => {
              this.$toasted.success(resp.message);
            });
        },
        nextQuestionIndex: "mainMessage",
        routine: () => {},
      },
      meeting: meetingQuestion(),
      ...referralQuestions(this, "mainMessage", SERVICE),
      missions: {
        name: "missions",
        text: `<div class="text-left"><strong>Puedes conseguir usos gratis realizando las siguientes acciones!</strong>`,
        type: "custom-component",
        component: MissionsView,
        answersLoaded: true,
        answers: [],
        dontShowNextButton: true,
        nextQuestionIndex: "mainMessage",
        routine: () => {},
      },
      document_type: {
        name: "document_type",
        text: "Selecciona el documento que quieres completar:",
        hint: "Escoge un documento",
        type: "options",
        single_choice: true,
        answersLoaded: false,
        nextQuestionIndex: "grade",
        answers: [],
        getAnswers(context) {
          if (this.answersLoaded) {
            return Promise.resolve(this.answers);
          }
          return individualDocumentService
            .listDocumentTypes()
            .then((data) => {
              // The role already confirmed for this feature ({value, name}) or
              // null — drives whether the `user_role` question is asked.
              if (context) context.savedUserRole = data.user_role || null;
              this.answers = [];
              const schoolId = data.school_id != null ? data.school_id : null;
              (data.document_types || []).forEach((dt) => {
                this.answers.push({
                  name: dt.name,
                  id: dt.id,
                  schoolId: schoolId,
                  isSelected: false,
                  class: "col-12 pb-1",
                  bootstrap_classes: "text-left px-2 font-weight-bold",
                  nextQuestionIndex: "grade",
                });
              });
              this.answersLoaded = true;
              return this.answers;
            })
            .catch(() => {
              this.answers = [];
              this.answersLoaded = true;
              return this.answers;
            });
        },
        routine() {},
      },
      grade: {
        name: "grade",
        text: "Selecciona el curso del estudiante:",
        hint: "Escoge un curso",
        type: "options",
        single_choice: true,
        answersLoaded: false,
        nextQuestionIndex: "diagnosis",
        answers: [],
        // Loads the chosen type's templates (kept on context for the diagnosis
        // step) + the grade list. The grade is kept in context because
        // templates use it as the level for special options. Routes to the
        // diagnosis step when there is more than one template/diagnostic to
        // choose from; with a single available template it is resolved
        // silently and we go straight to filling.
        getAnswers(context) {
          const dt = (context && context.document_type) || {};
          return Promise.all([
            individualDocumentService
              .listDocumentTemplates({ document_type_id: dt.id })
              .catch(() => ({ templates: [] })),
            informationService.getPieGrades().catch(() => ({ grades: [] })),
          ]).then(([tplData, gradeData]) => {
            const templates = (tplData && tplData.templates) || [];
            dt.templates = templates;
            // Clear any diagnosis chosen in a previous flow; it's re-set below
            // (silent) or by the diagnosis step.
            if (context) context.diagnosis = null;
            // Main diagnostics across the templates = the diagnosis options.
            const diagOptions = mainDiagnosisTemplates(templates);
            // The role step is skipped when the user already confirmed a role
            // (reused from the instance); seed it so the fill step still has it.
            const savedRole = (context && context.savedUserRole) || null;
            const afterDiag = savedRole ? "fill_first_page" : "user_role";
            if (savedRole && context) context.user_role = savedRole;
            // Only one option -> skip the diagnosis question, choose it
            // silently; otherwise ask which diagnostic.
            dt.resolvedTemplateId = null;
            dt.resolvedDiagnostic = null;
            let next = "diagnosis";
            if (diagOptions.length <= 1) {
              const only = diagOptions[0] || null;
              dt.resolvedTemplateId = only ? only.template_id : null;
              dt.resolvedDiagnostic = only ? only.diagnostic : null;
              next = afterDiag;
            }
            const grades = (gradeData && gradeData.grades) || [];
            this.answers = grades.map((g) => ({
              name: g[1],
              value: g[0],
              isSelected: false,
              class: "col-6 pb-1",
              bootstrap_classes: "text-center font-weight-bold",
              nextQuestionIndex: next,
            }));
            return this.answers;
          });
        },
        routine() {},
      },
      diagnosis: {
        name: "diagnosis",
        text: "Selecciona el diagnóstico del documento:",
        hint: "Escoge un diagnóstico",
        type: "options",
        single_choice: true,
        answersLoaded: false,
        nextQuestionIndex: "user_role",
        answers: [],
        // Always asked before rendering. Options are the UNIQUE diagnostic
        // values across the type's templates; each carries the template_id to
        // render for that diagnostic so the fill step loads it directly.
        getAnswers(context) {
          const templates =
            (context &&
              context.document_type &&
              context.document_type.templates) ||
            [];
          // Skip the role step when a role is already confirmed (seed it so the
          // fill step still receives it).
          const savedRole = (context && context.savedUserRole) || null;
          const next = savedRole ? "fill_first_page" : "user_role";
          if (savedRole && context) context.user_role = savedRole;
          this.answers = mainDiagnosisTemplates(templates).map((t) => ({
            name: t.diagnostic_label,
            value: t.diagnostic,
            templateId: t.template_id,
            isSelected: false,
            class: "col-12 pb-1",
            bootstrap_classes: "text-left px-2 font-weight-bold",
            nextQuestionIndex: next,
          }));
          return Promise.resolve(this.answers);
        },
        routine() {},
      },
      // The user's role for THIS document. Its value is a RoleEnum id; the fill
      // step offers the logged-in user as the option of the USER_SELECTOR bound
      // to this role (the other selectors stay empty). `null` = not specified.
      user_role: {
        name: "user_role",
        text: "¿Cuál es tu rol en este documento? Lo usaremos para completar tus datos en el campo correspondiente.",
        hint: "Escoge tu rol",
        type: "options",
        single_choice: true,
        answersLoaded: false,
        nextQuestionIndex: "fill_first_page",
        answers: [],
        getAnswers() {
          const roles = [
            ["Educador/a diferencial", 6],
            ["Psicopedagogo/a", 1],
            ["Psicólogo/a", 2],
            ["Fonoaudiólogo/a", 5],
            ["Terapeuta Ocupacional", 12],
            ["Kinesiólogo/a", 17],
            ["Coordinador/a PIE", 3],
            ["Director/a", 0],
            ["Profesor/a jefe", 10],
          ];
          this.answers = roles.map(([name, value]) => ({
            name,
            value,
            isSelected: false,
            class: "col-6 pb-1",
            bootstrap_classes: "text-center font-weight-bold",
            nextQuestionIndex: "fill_first_page",
          }));
          this.answers.push({
            name: "Prefiero no indicarlo / otro",
            value: null,
            isSelected: false,
            class: "col-12 pb-1",
            bootstrap_classes: "text-center font-weight-bold",
            nextQuestionIndex: "fill_first_page",
          });
          this.answersLoaded = true;
          return Promise.resolve(this.answers);
        },
        routine() {},
      },
      fill_first_page: {
        name: "fill_first_page",
        text: "La primera página del documento corresponde a la identificación (datos del estudiante y de los profesionales). Son principalmente campos de texto que deberás escribir. ¿Quieres completar esta primera página?",
        hint: "Elige una opción",
        type: "options",
        single_choice: true,
        answersLoaded: false,
        nextQuestionIndex: "fill_document",
        answers: [],
        getAnswers() {
          this.answers = [
            {
              name: "Sí, completar la primera página",
              value: true,
              isSelected: false,
              class: "col-12 pb-1",
              bootstrap_classes: "text-left px-2 font-weight-bold",
              nextQuestionIndex: "fill_document",
            },
            {
              name: "No, omitir la primera página",
              value: false,
              isSelected: false,
              class: "col-12 pb-1",
              bootstrap_classes: "text-left px-2 font-weight-bold",
              nextQuestionIndex: "student_name",
            },
          ];
          this.answersLoaded = true;
          return Promise.resolve(this.answers);
        },
        routine() {},
      },
      // Asked only when the user skipped the first (identification) page, so the
      // student's name still makes it into the document (variable NYA_E).
      student_name: {
        name: "student_name",
        text: "¿Cuál es el nombre del estudiante? Lo incluiremos en el documento descargado.",
        hint: "Escribe el nombre del estudiante",
        type: "text-area",
        single_choice: false,
        answersLoaded: false,
        nextQuestionIndex: "fill_document",
        answers: [],
        getAnswers() {
          return Promise.resolve([]);
        },
        routine() {},
      },
      fill_document: {
        name: "fill_document",
        text: "Completa el documento y descarga el Word.",
        type: "custom-component",
        component: DocumentFillStep,
        answersLoaded: false,
        answers: [],
        isFinal: false,
        dontShowNextButton: true,
        nextQuestionIndex: "document_type",
        getAnswers() {
          return new Promise((resolve) => {
            setTimeout(() => resolve([]), 300);
          });
        },
        routine() {},
      },
    };
  },
  mounted() {
    let question = this.questions[this.currentQuestionIndex];
    this.messages.push(question);
    this.context[question.name] = {};
    this.$store.commit("chatbot/clear");
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
  },
  methods: {
    handleCardClick(answer, msgidx) {
      if (this.messages[msgidx].name !== this.currentQuestion.name) return;
      if (this.currentQuestion.single_choice) {
        this.messages[msgidx].answers.forEach((ans) => {
          ans.isSelected = false;
        });
      }
      answer.isSelected = !answer.isSelected;
      this.selectAnswer();
    },
    handleIncrease() {},
    handleDecrease() {},
    async continueChat() {
      if (this.currentQuestion.type === "options") {
        if (this.currentQuestion.single_choice) {
          this.context[this.currentQuestion.name] =
            this.currentQuestion.answers.find((ans) => ans.isSelected);
          this.$store.commit("chatbot/updateMessage", {
            name: this.currentQuestion.name,
            key: "answer",
            value: this.context[this.currentQuestion.name],
          });
          if (this.context[this.currentQuestion.name].redirect) {
            window.location.href = this.context[this.currentQuestion.name].redirect;
            return;
          }
        } else {
          this.context[this.currentQuestion.name] =
            this.currentQuestion.answers.filter((ans) => ans.isSelected);
        }
      }

      // Persist the chosen role onto the user (same across every document), so
      // it doesn't need to be re-asked. Fire-and-forget: the frontend already
      // has the value for this document, so a failed save never blocks the chat.
      if (this.currentQuestion.name === "user_role") {
        const sel = this.context.user_role;
        if (sel && sel.value != null) {
          individualDocumentService
            .setUserRole({ role: sel.value })
            .catch(() => {});
        }
      }

      this.messages.slice(-1)[0].answered = true;
      if (!this.currentQuestion.isFinal) {
        await this.currentQuestion.routine();
        if (!this.currentQuestion.single_choice) {
          this.currentQuestionIndex =
            typeof this.currentQuestion.nextQuestionIndex === "function"
              ? this.currentQuestion.nextQuestionIndex(this.context)
              : this.currentQuestion.nextQuestionIndex;
        } else {
          this.currentQuestionIndex =
            this.context[this.currentQuestion.name] &&
            this.context[this.currentQuestion.name].nextQuestionIndex
              ? this.context[this.currentQuestion.name].nextQuestionIndex
              : this.currentQuestion.nextQuestionIndex;
        }
        if (this.currentQuestionIndex === "reset") {
          this.resetChat();
        } else {
          this.loadQuestion(this.currentQuestionIndex);
        }
        this.context["currentQuestionIndex"] = this.currentQuestionIndex;
      }
    },
    loadQuestion(questionIndex) {
      try {
        let question = this.questions[questionIndex];
        this.context[question.name] = {};
        this.messages.push(question);
      } catch (error) {
        console.log(error);
      }
    },
    goToStep(stepName) {
      if (stepName === "reset") {
        this.resetChat();
        return;
      }
      this.messages.pop();
      this.messages.pop();
      let question = this.questions[stepName];
      if (question) {
        question.answered = false;
      }
      this.currentQuestionIndex = stepName;
      this.$nextTick(() => {
        this.loadQuestion(stepName);
      });
    },
    selectAnswer() {
      if (this.currentQuestion.single_choice) {
        this.continueChat();
      }
    },
    scrollToBottom() {
      let container = document.querySelector(".global-container");
      if (container) window.scrollTo(0, container.scrollHeight);
    },
    scrollToMsg(name) {
      if (skipAutoScroll(this.messages, name)) return;
      const msgRef = this.$refs[`msgRef-${name}`];
      if (!msgRef || !msgRef.length) return;
      // El último, no el primero: el menú se vuelve a montar en cada vuelta y
      // `$refs` los acumula, así que `[0]` mandaba el scroll al menú de arriba
      // de todo en vez de al que se acaba de agregar.
      const parentMsg = msgRef[msgRef.length - 1].$el.parentNode;
      parentMsg.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    },
    resetChat() {
      this.$emit("toggle-reload");
    },
    handleCheckbox(msgidx, value) {
      this.messages[msgidx].optionalCheckbox = value;
      this.context[this.currentQuestion.name].optionalCheckbox = value;
    },
    setTextInput(msgidx, value) {
      this.messages[msgidx].textInput = value;
      this.context[this.currentQuestion.name].textInput = value;
    },
    handleSelectorChange(msgidx, idx, value) {
      this.messages[msgidx].answers[idx].selected = value;
      this.context[this.currentQuestion.name].answers =
        this.messages[msgidx].answers;
    },
  },
};
</script>

<style scoped>
.chat-box {
  /* Full available width, no side margins — gives the embedded DocumentOnline
     room to render (a fluid container drops bootstrap's max-width cap). Only a
     small top margin is kept. */
  margin: 10px 0 0 0;
  width: 100%;
  max-width: 100%;
  align-items: space-between;
  justify-content: space-between;
}
.reset-button {
  border-radius: 5px;
  border: 2pt solid rgb(0, 0, 0) !important;
  font-family: "Ubuntu Medium", Arial, sans-serif;
  font-weight: bold;
  cursor: pointer;
}
</style>
